import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Search, ArrowRight, Mail } from "lucide-react";
import { BUSINESS, IMG } from "../data/content";
import { SITE_URL } from "../data/locations";

// Toast gift-card / signup pages block iframe embedding (X-Frame-Options + reCAPTCHA),
// so each action opens in a NEW TAB — the Detour page stays open behind it.
const GiftCards = () => {
  const url = `${SITE_URL}/gift-cards`;
  const title = "Detour Coffee Co. Gift Cards | Buy & Check Balance — Arlington VA";
  const description =
    "Buy a Detour Coffee Co. e-gift card or check your gift card balance. The perfect gift for coffee, brunch & Mediterranean food lovers in Arlington, Virginia.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Gift Cards", item: url },
    ],
  };

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={IMG.spread} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <article className="bg-background" data-testid="gift-cards-page">
        {/* Hero */}
        <header className="relative bg-[#241812] pt-32 pb-16 sm:pt-40 sm:pb-24 overflow-hidden">
          <div className="absolute inset-0 opacity-25">
            <img src={IMG.cappuccinoReal} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#241812] via-[#241812]/70 to-[#241812]/40" />
          </div>
          <div className="relative max-w-4xl mx-auto px-6 md:px-12">
            <nav className="flex items-center gap-2 text-xs text-background/60 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-highlight transition-colors">Home</Link>
              <span>/</span>
              <span className="text-highlight">Gift Cards</span>
            </nav>
            <p className="text-xs sm:text-sm uppercase tracking-[0.24em] font-semibold text-highlight mb-4 flex items-center gap-3">
              <Gift size={16} /> Detour Coffee Co.
            </p>
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              Give the gift of <span className="text-highlight">Detour</span>
            </motion.h1>
            <p className="mt-6 max-w-2xl text-background/80 text-base sm:text-lg leading-relaxed">
              A Detour Coffee Co. gift card is the perfect way to treat someone to specialty coffee, weekend brunch and Mediterranean favorites in the heart of Arlington. Delivered by email — spend in-store or online.
            </p>
          </div>
        </header>

        {/* Cards */}
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-14 sm:py-20">
          <p className="text-sm text-foreground/55 mb-6">
            Each option opens in a new tab with our secure Toast page — this Detour page stays open right here.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Buy */}
            <div className="rounded-3xl border border-surface bg-surface/40 p-8 flex flex-col" data-testid="giftcard-buy-card">
              <div className="h-12 w-12 rounded-full bg-highlight/15 text-highlight flex items-center justify-center">
                <Gift size={22} />
              </div>
              <h2 className="mt-5 text-2xl tracking-tight text-foreground">Buy a gift card</h2>
              <p className="mt-3 text-foreground/70 leading-relaxed flex-1">
                Choose any amount and send an e-gift card straight to their inbox — great for birthdays, thank-yous, teachers and coffee lovers.
              </p>
              <a
                href={BUSINESS.giftCardUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="giftcard-buy-btn"
                className="group mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-highlight text-foreground px-7 py-3.5 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03]"
              >
                Buy an e-gift card <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>

            {/* Balance */}
            <div className="rounded-3xl border border-surface bg-surface/40 p-8 flex flex-col" data-testid="giftcard-balance-card">
              <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <Search size={22} />
              </div>
              <h2 className="mt-5 text-2xl tracking-tight text-foreground">Check your balance</h2>
              <p className="mt-3 text-foreground/70 leading-relaxed flex-1">
                Already have a Detour gift card? Look up your remaining balance any time before you order.
              </p>
              <a
                href={BUSINESS.giftCardBalanceUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="giftcard-balance-btn"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-foreground/25 text-foreground px-7 py-3.5 text-sm font-semibold tracking-wide transition-colors hover:bg-foreground hover:text-background"
              >
                <Search size={16} /> Check gift card balance
              </a>
            </div>
          </div>

          {/* Marketing signup */}
          <div className="mt-8 rounded-3xl bg-primary text-background p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="giftcard-signup">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-highlight font-semibold mb-2 flex items-center gap-2">
                <Mail size={15} /> Stay in the loop
              </p>
              <h3 className="text-xl sm:text-2xl tracking-tight">Get email & SMS offers</h3>
              <p className="mt-2 text-background/70 max-w-md leading-relaxed text-sm">
                Be first to hear about seasonal drinks, events and exclusive deals at Detour.
              </p>
            </div>
            <a
              href={BUSINESS.marketingSignupUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="giftcard-signup-btn"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-highlight text-foreground px-7 py-3.5 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03]"
            >
              Sign up <ArrowRight size={17} />
            </a>
          </div>

          <Link
            to="/"
            className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-highlight hover:opacity-80"
            data-testid="giftcard-back-home"
          >
            <ArrowRight size={16} className="rotate-180" /> Back to Detour Coffee Co.
          </Link>
        </div>
      </article>
    </>
  );
};

export default GiftCards;
